import { useTranslation } from 'react-i18next';
import { useModalBackClose } from './useModalBackClose';

export interface FireTarget {
  orderUuid: string;
  round: number;
  label: string;
  dishCount: number;
}

export interface TicketFireConfirmProps {
  target: FireTarget;
  /** Resolves the display name for the round (label or fallback « R{{n}} ») —
   *  same helper `TicketCommandes` uses for the held-round row itself. */
  heldRoundName: (round: number, label: string) => string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * SC-902 T7 F1 — extracted out of `TicketCommandes` so `useModalBackClose`
 * only runs while this dialog is actually mounted (`fireTarget` set), same
 * idiom as `TicketCartModal` / `TicketOptionsModal`. Previously the hook was
 * called unconditionally alongside the pre-existing `decrement-confirm` one,
 * pushing a second history entry on every mount regardless of whether the
 * dialog was open — the decrement dialog then needed two back presses to
 * close instead of one.
 */
export default function TicketFireConfirm({
  target,
  heldRoundName,
  submitting,
  onCancel,
  onConfirm,
}: TicketFireConfirmProps) {
  const { t } = useTranslation();

  useModalBackClose('fire-confirm', onCancel);

  return (
    <div
      data-testid="fire-confirm"
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-[1190] flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--w-scrim)' }}
    >
      <div className="bg-paper border-2 border-ink rounded-btn p-5 max-w-sm w-full flex flex-col gap-4">
        <p className="font-sans text-lg font-bold">{t('waiter.ticket.commandes.fireConfirm.title')}</p>
        <p className="font-sans text-[18px]">
          {target.dishCount === 1
            ? t('waiter.ticket.commandes.fireConfirm.bodyOne', {
                name: heldRoundName(target.round, target.label),
                count: target.dishCount,
              })
            : t('waiter.ticket.commandes.fireConfirm.bodyOther', {
                name: heldRoundName(target.round, target.label),
                count: target.dishCount,
              })}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="fire-confirm-cancel"
            disabled={submitting}
            onClick={onCancel}
            className="h-12 flex-1 border-2 border-ink rounded-btn font-sans text-[18px] font-bold disabled:opacity-40"
          >
            {t('waiter.ticket.commandes.fireConfirm.cancel')}
          </button>
          <button
            type="button"
            data-testid="fire-confirm-submit"
            disabled={submitting}
            onClick={onConfirm}
            className="h-12 flex-1 rounded-btn font-sans text-[18px] font-bold text-white disabled:opacity-40"
            style={{ backgroundColor: 'var(--w-info)' }}
          >
            {t('waiter.ticket.commandes.fireConfirm.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
